import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { formatDate } from '../utils/formatters';
import { MatchContextModal } from '../components/shared/MatchContextModal';
import { TennisContextModal } from '../components/shared/TennisContextModal';
import './UpcomingPage.css';

const DOT_COLORS = { H: '#60a5fa', D: '#9ca3af', A: '#f87171' };
const CONF_COLOR = (conf) => conf >= 0.70 ? '#10b981' : conf >= 0.55 ? '#d97706' : '#ef4444';
const PAGE_SIZE = 20;
const API_URL = process.env.REACT_APP_API_URL || '/api';

const SURFACE_COLORS = { Hard: '#3b82f6', Clay: '#f97316', Grass: '#22c55e', Carpet: '#8b5cf6' };

function getWeekLabel(dateStr) {
    const date = new Date(dateStr);
    const day  = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(date);
    monday.setDate(diff);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    return `${fmt(monday)} – ${fmt(sunday)}`;
}

function groupByWeek(matches) {
    const groups = {};
    matches.forEach(m => {
        const label = getWeekLabel(m.date);
        if (!groups[label]) groups[label] = [];
        groups[label].push(m);
    });
    return groups;
}

// ── Baseball upcoming component ──────────────────────────────────────────────

function BaseballUpcomingContent() {
    const { t, i18n } = useTranslation();
    const [games, setGames]     = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/baseball/upcoming`);
            setGames(res.data || []);
        } catch (e) {
            setGames([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, []);

    // Group by date
    const byDate = games.reduce((acc, g) => {
        const d = g.game_date;
        if (!acc[d]) acc[d] = [];
        acc[d].push(g);
        return acc;
    }, {});

    return (
        <div className="upcoming-section">
            <div className="section-header">
                <h2>
                    {t('upcoming.title')} — Baseball / MLB
                    {games.length > 0 && (
                        <span className="picks-total-badge">{games.length}</span>
                    )}
                </h2>
                <button onClick={load} className="refresh-button">{t('common.refresh')}</button>
            </div>

            {loading ? (
                <div className="empty-state"><p>{t('common.loading')}</p></div>
            ) : games.length === 0 ? (
                <div className="empty-state">
                    <p>No upcoming MLB games found</p>
                    <p className="subtitle">Run the baseball pipeline to generate predictions</p>
                </div>
            ) : (
                Object.entries(byDate).map(([date, dayGames]) => (
                    <div key={date} className="week-group">
                        <div className="week-label">
                            <div className="week-label-left">
                                <span className="week-label-tag">MLB</span>
                                <span className="week-label-date">
                                    {new Date(date + 'T12:00:00').toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'short' })}
                                </span>
                            </div>
                            <div className="week-label-right">
                                <span className="week-count-num">{dayGames.length}</span>
                                <span className="week-count-label">{t('upcoming.matches_count')}</span>
                            </div>
                        </div>

                        <div className="upcoming-list">
                            {dayGames.map(game => {
                                const probH = Math.round(game.prob_home * 100);
                                const probA = Math.round(game.prob_away * 100);
                                const conf  = game.confidence || game.prob_home;
                                const isHomeWinner = game.predicted_winner === 'home';
                                return (
                                    <div key={game.id} className="upcoming-card">
                                        <div className="upcoming-pick-header">
                                            <div className="upcoming-header-left">
                                                <span className="league-tag league-tag--Baseball">MLB</span>
                                                <span className="pick-date">
                                                    {new Date(date + 'T12:00:00').toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })}
                                                </span>
                                            </div>
                                            <div className="upcoming-header-right">
                                                <span
                                                    className="upcoming-conf-big"
                                                    style={{ color: CONF_COLOR(conf) }}
                                                >
                                                    {Math.round(conf * 100)}%
                                                </span>
                                                <span className="pick-conf-label">{t('common.confidence')}</span>
                                            </div>
                                        </div>

                                        <div className="upcoming-pick-body">
                                            <div className="pick-teams">
                                                <div className={`pick-team-name home${isHomeWinner ? ' pick-team--predicted' : ''}`}>
                                                    {game.home_team}
                                                </div>
                                                <div className="pick-separator">{t('common.vs')}</div>
                                                <div className={`pick-team-name away${!isHomeWinner ? ' pick-team--predicted' : ''}`}>
                                                    {game.away_team}
                                                </div>
                                            </div>

                                            <div className="pick-bar">
                                                <div className="pick-bar-seg home" style={{ width: `${probH}%` }} />
                                                <div className="pick-bar-seg away" style={{ width: `${probA}%` }} />
                                            </div>
                                            <div className="pick-seg-labels">
                                                <div className="pick-seg-label">
                                                    <span className="seg-val">{probH}%</span>
                                                </div>
                                                <div className="pick-seg-label right">
                                                    <span className="seg-val">{probA}%</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="upcoming-pick-footer">
                                            <div className="pick-pred-type">
                                                <span className="pick-dot" style={{ background: '#10b981' }} />
                                                <span className="pick-pred-label">{t('historial.pred_label')}</span>
                                            </div>
                                            <span className="pick-pred-team">
                                                {isHomeWinner ? game.home_team : game.away_team}
                                            </span>
                                        </div>

                                        {(game.home_starter || game.away_starter) && (
                                            <div style={{ padding: '4px 12px 8px', fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', display: 'flex', gap: '16px' }}>
                                                {game.home_starter && <span>SP: {game.home_starter}</span>}
                                                {game.away_starter && <span>SP: {game.away_starter}</span>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

// ── Tennis upcoming component ────────────────────────────────────────────────

function TennisUpcomingContent() {
    const { t, i18n } = useTranslation();
    const [matches, setMatches]       = useState([]);
    const [loading, setLoading]       = useState(false);
    const [surfFilter, setSurfFilter] = useState('all');
    const [selectedPick, setSelectedPick] = useState(null);

    const SURF_FILTERS = [
        { id: 'all',   label: t('tennis.surface_all') },
        { id: 'Hard',  label: t('tennis.surface_hard') },
        { id: 'Clay',  label: t('tennis.surface_clay') },
        { id: 'Grass', label: t('tennis.surface_grass') },
    ];

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ days: 60 });
            if (surfFilter !== 'all') params.set('surface', surfFilter);
            const res = await axios.get(`${API_URL}/tennis/upcoming?${params}`);
            setMatches(res.data.matches || []);
        } catch (e) {
            setMatches([]);
        } finally {
            setLoading(false);
        }
    }, [surfFilter]);

    useEffect(() => { load(); }, [surfFilter]);

    // Group by date
    const byDate = matches.reduce((acc, m) => {
        if (!acc[m.date]) acc[m.date] = [];
        acc[m.date].push(m);
        return acc;
    }, {});

    return (
        <div className="upcoming-section">
            <div className="section-header">
                <h2>
                    {t('upcoming.title')} — {t('leagues.Tennis')}
                    {matches.length > 0 && (
                        <span className="picks-total-badge">{matches.length}</span>
                    )}
                </h2>
                <button onClick={load} className="refresh-button">{t('common.refresh')}</button>
            </div>

            <div className="upcoming-filters">
                <div className="upcoming-filter-group">
                    <span className="upcoming-filter-label">{t('tennis.surface_all')}</span>
                    <div className="upcoming-filter-pills">
                        {SURF_FILTERS.map(f => (
                            <button
                                key={f.id}
                                className={`filter-btn ${surfFilter === f.id ? 'active' : ''}`}
                                onClick={() => setSurfFilter(f.id)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="empty-state"><p>{t('common.loading')}</p></div>
            ) : matches.length === 0 ? (
                <div className="empty-state">
                    <p>{t('tennis.no_picks')}</p>
                    <p className="subtitle">{t('tennis.needs_update')}</p>
                </div>
            ) : (
                Object.entries(byDate).map(([date, dayMatches]) => (
                    <div key={date} className="week-group">
                        <div className="week-label">
                            <div className="week-label-left">
                                <span className="week-label-tag">ATP</span>
                                <span className="week-label-date">
                                    {new Date(date + 'T12:00:00').toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'short' })}
                                </span>
                            </div>
                            <div className="week-label-right">
                                <span className="week-count-num">{dayMatches.length}</span>
                                <span className="week-count-label">{t('upcoming.matches_count')}</span>
                            </div>
                        </div>

                        <div className="upcoming-list">
                            {dayMatches.map(match => {
                                const hasPred   = match.has_prediction;
                                const probW     = hasPred ? Math.round(match.prob_winner   * 100) : null;
                                const probL     = hasPred ? Math.round(match.prob_underdog * 100) : null;
                                const isP1Winner = hasPred && match.predicted_winner === match.p1_name;
                                return (
                                    <div key={match.match_id} className="upcoming-card"
                                        onClick={() => hasPred && setSelectedPick(match)}
                                        style={{ cursor: hasPred ? 'pointer' : 'default' }}>
                                        <div className="upcoming-pick-header">
                                            <div className="upcoming-header-left">
                                                <span
                                                    className="pick-surface-badge"
                                                    style={{ background: SURFACE_COLORS[match.surface] || '#6b7280' }}
                                                >
                                                    {match.surface}
                                                </span>
                                                <span className="week-count-label" style={{ marginLeft: 6 }}>
                                                    {match.tournament}
                                                </span>
                                                {match.round && (
                                                    <span className="week-count-label" style={{ marginLeft: 6 }}>
                                                        {match.round}
                                                    </span>
                                                )}
                                            </div>
                                            {hasPred && (
                                                <div className="upcoming-header-right">
                                                    <span
                                                        className="upcoming-conf-big"
                                                        style={{ color: CONF_COLOR(match.confidence) }}
                                                    >
                                                        {Math.round(match.confidence * 100)}%
                                                    </span>
                                                    <span className="pick-conf-label">{t('common.confidence')}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="upcoming-pick-body">
                                            <div className="pick-teams">
                                                <div className={`pick-team-name home${isP1Winner ? ' pick-team--predicted' : ''}`}>
                                                    {match.p1_name}
                                                </div>
                                                <div className="pick-separator">{t('common.vs')}</div>
                                                <div className={`pick-team-name away${hasPred && !isP1Winner ? ' pick-team--predicted' : ''}`}>
                                                    {match.p2_name}
                                                </div>
                                            </div>

                                            {hasPred ? (
                                                <>
                                                    <div className="pick-bar">
                                                        <div className="pick-bar-seg home" style={{ width: `${isP1Winner ? probW : probL}%` }} />
                                                        <div className="pick-bar-seg away" style={{ width: `${isP1Winner ? probL : probW}%` }} />
                                                    </div>
                                                    <div className="pick-seg-labels">
                                                        <div className="pick-seg-label">
                                                            <span className="seg-val">{isP1Winner ? probW : probL}%</span>
                                                        </div>
                                                        <div className="pick-seg-label right">
                                                            <span className="seg-val">{isP1Winner ? probL : probW}%</span>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="no-prediction">{t('upcoming.no_prediction')}</div>
                                            )}
                                        </div>

                                        {hasPred && (
                                            <div className="upcoming-pick-footer">
                                                <div className="pick-pred-type">
                                                    <span className="pick-dot" style={{ background: '#10b981' }} />
                                                    <span className="pick-pred-label">{t('historial.pred_label')}</span>
                                                </div>
                                                <span className="pick-pred-team">{match.predicted_winner}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}

            {selectedPick && (
                <TennisContextModal pick={selectedPick} onClose={() => setSelectedPick(null)} />
            )}
        </div>
    );
}

export function UpcomingPage({ onRefresh, fetchUpcomingMatches, selectedLeague, fetchMatchContext, isTennis = false, isBaseball = false }) {
    const { t } = useTranslation();

    const CONFIDENCE_FILTERS = [
        { id: 'all',    label: t('upcoming.filter_all'),    min: null, max: null },
        { id: 'high',   label: t('upcoming.filter_high'),   min: 0.70, max: null },
        { id: 'medium', label: t('upcoming.filter_medium'), min: 0.60, max: 0.70 },
        { id: 'low',    label: t('upcoming.filter_low'),    min: null, max: 0.60 },
    ];

    const RESULT_FILTERS = [
        { id: 'all', label: t('upcoming.filter_all') },
        { id: 'H',   label: t('results.H_short') },
        { id: 'D',   label: t('results.D_short') },
        { id: 'A',   label: t('results.A_short') },
    ];

    const [matches, setMatches]             = useState([]);
    const [totalCount, setTotalCount]       = useState(0);
    const [page, setPage]                   = useState(0);
    const [loading, setLoading]             = useState(false);
    const [confFilter, setConfFilter]       = useState('all');
    const [resultFilter, setResultFilter]   = useState('all');
    const [selectedMatch, setSelectedMatch] = useState(null);

    const loadMatches = useCallback(async (p = 0, conf = confFilter, result = resultFilter) => {
        setLoading(true);
        const confObj = CONFIDENCE_FILTERS.find(f => f.id === conf) || {};
        const filters = {
            predicted_result: result !== 'all' ? result : null,
            min_confidence:   confObj.min,
            max_confidence:   confObj.max,
        };
        const res = await fetchUpcomingMatches(p * PAGE_SIZE, filters);
        setMatches(res.matches || []);
        setTotalCount(res.totalCount || 0);
        setLoading(false);
    }, [confFilter, resultFilter, fetchUpcomingMatches, selectedLeague]);

    useEffect(() => {
        if (isBaseball || isTennis) return;
        setPage(0);
        loadMatches(0, confFilter, resultFilter);
    }, [confFilter, resultFilter, selectedLeague, isBaseball, isTennis]);

    const handleConfFilter = (id) => { setConfFilter(id); setPage(0); };
    const handleResultFilter = (id) => { setResultFilter(id); setPage(0); };

    const handlePrev = () => { const p = page - 1; setPage(p); loadMatches(p); };
    const handleNext = () => { const p = page + 1; setPage(p); loadMatches(p); };

    // Normaliza el match para que el modal reciba siempre el mismo formato
    // que HomePage (probabilities a nivel raíz, no anidadas en .prediction)
    function normalizeForModal(match) {
        if (!match.prediction) return match;
        return {
            ...match,
            predicted_result: match.prediction.predicted_result,
            probabilities:    match.prediction.probabilities,
            confidence:       match.prediction.confidence,
            model_version:    match.prediction.model_version,
        };
    }

    if (isBaseball) return <BaseballUpcomingContent />;
    if (isTennis) return <TennisUpcomingContent />;

    const weekGroups = groupByWeek(matches);
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);
    const hasPrev    = page > 0;
    const hasNext    = (page + 1) * PAGE_SIZE < totalCount;

    return (
        <div className="upcoming-section">
            <div className="section-header">
                <h2>
                    {t('upcoming.title')}
                    {totalCount > 0 && (
                        <span className="picks-total-badge">{totalCount}</span>
                    )}
                </h2>
                <button onClick={() => loadMatches(page)} className="refresh-button">
                    {t('common.refresh')}
                </button>
            </div>

            <div className="upcoming-filters">
                <div className="upcoming-filter-group">
                    <span className="upcoming-filter-label">{t('upcoming.filter_confidence')}</span>
                    <div className="upcoming-filter-pills">
                        {CONFIDENCE_FILTERS.map(f => (
                            <button
                                key={f.id}
                                className={`filter-btn ${confFilter === f.id ? 'active' : ''}`}
                                onClick={() => handleConfFilter(f.id)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="upcoming-filter-group">
                    <span className="upcoming-filter-label">{t('upcoming.filter_prediction')}</span>
                    <div className="upcoming-filter-pills">
                        {RESULT_FILTERS.map(f => (
                            <button
                                key={f.id}
                                className={`filter-btn ${resultFilter === f.id ? 'active' : ''}`}
                                onClick={() => handleResultFilter(f.id)}
                            >
                                {t(`results.${f.id}`) !== `results.${f.id}` && f.id !== 'all'
                                    ? t(`results.${f.id}`)
                                    : f.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="empty-state"><p>{t('common.loading')}</p></div>
            ) : matches.length === 0 ? (
                <div className="empty-state"><p>{t('upcoming.empty')}</p></div>
            ) : (
                <>
                    {Object.entries(weekGroups).map(([weekLabel, weekMatches]) => (
                        <div key={weekLabel} className="week-group">
                            <div className="week-label">
                                <div className="week-label-left">
                                    <span className="week-label-tag">{t('upcoming.matchday')}</span>
                                    <span className="week-label-date">{weekLabel}</span>
                                </div>
                                <div className="week-label-right">
                                    <span className="week-count-num">{weekMatches.length}</span>
                                    <span className="week-count-label">{t('upcoming.matches_count')}</span>
                                </div>
                            </div>

                            <div className="upcoming-list">
                                {weekMatches.map((match) => {
                                    const pred  = match.prediction?.predicted_result;
                                    const conf  = match.prediction?.confidence ?? 0;
                                    const probH = Math.round((match.prediction?.probabilities?.home ?? 0) * 100);
                                    const probD = Math.round((match.prediction?.probabilities?.draw ?? 0) * 100);
                                    const probA = Math.round((match.prediction?.probabilities?.away ?? 0) * 100);
                                    const winnerTeam = pred === 'H' ? match.home_team
                                                     : pred === 'A' ? match.away_team
                                                     : null;

                                    return (
                                        <div
                                            key={match.match_id}
                                            className="upcoming-card"
                                            onClick={() => setSelectedMatch(normalizeForModal(match))}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="upcoming-pick-header">
                                                <div className="upcoming-header-left">
                                                    <span className={`league-tag league-tag--${match.league}`}>
                                                        {t(`leagues.${match.league}`)}
                                                    </span>
                                                    <span className="pick-date">{formatDate(match.date)}</span>
                                                </div>
                                                {match.prediction && (
                                                    <div className="upcoming-header-right">
                                                        <span
                                                            className="upcoming-conf-big"
                                                            style={{ color: CONF_COLOR(conf) }}
                                                        >
                                                            {Math.round(conf * 100)}%
                                                        </span>
                                                        <span className="pick-conf-label">{t('common.confidence')}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="upcoming-pick-body">
                                                <div className="pick-teams">
                                                    <div className="pick-team-name home">{match.home_team}</div>
                                                    <div className="pick-separator">{t('common.vs')}</div>
                                                    <div className="pick-team-name away">{match.away_team}</div>
                                                </div>

                                                {match.prediction ? (
                                                    <>
                                                        <div className="pick-bar">
                                                            <div className="pick-bar-seg home" style={{ width: `${probH}%` }} />
                                                            <div className="pick-bar-seg draw" style={{ width: `${probD}%` }} />
                                                            <div className="pick-bar-seg away" style={{ width: `${probA}%` }} />
                                                        </div>
                                                        <div className="pick-seg-labels">
                                                            <div className="pick-seg-label">
                                                                <span className="seg-letter">{t('results.H_letter')}</span>
                                                                <span className="seg-val">{probH}%</span>
                                                            </div>
                                                            <div className="pick-seg-label center">
                                                                <span className="seg-letter">{t('results.D_letter')}</span>
                                                                <span className="seg-val">{probD}%</span>
                                                            </div>
                                                            <div className="pick-seg-label right">
                                                                <span className="seg-letter">{t('results.A_letter')}</span>
                                                                <span className="seg-val">{probA}%</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="no-prediction">{t('upcoming.no_prediction')}</div>
                                                )}
                                            </div>

                                            {match.prediction && (
                                                <div className="upcoming-pick-footer">
                                                    <div className="pick-pred-type">
                                                        <span className="pick-dot" style={{ background: DOT_COLORS[pred] || '#9ca3af' }} />
                                                        <span className="pick-pred-label">{t(`results.${pred}`)}</span>
                                                    </div>
                                                    <span className="pick-pred-team">{winnerTeam || t('results.D_short')}</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {totalPages > 1 && (
                        <div className="picks-pagination">
                            <button className="page-btn" onClick={handlePrev} disabled={!hasPrev}>
                                {t('historial.prev')}
                            </button>
                            <span className="page-info">
                                {page + 1} / {totalPages}
                                <span className="page-total"> · {totalCount} {t('upcoming.matches_count')}</span>
                            </span>
                            <button className="page-btn" onClick={handleNext} disabled={!hasNext}>
                                {t('historial.next')}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* MODAL CONTEXTO PRE-PARTIDO */}
            {selectedMatch && (
                <MatchContextModal
                    match={selectedMatch}
                    onClose={() => setSelectedMatch(null)}
                    fetchMatchContext={fetchMatchContext}
                />
            )}
        </div>
    );
}